import { createSelector } from "reselect";
import CalcType from 'state/Constants/CalcType';
import SelectionState from "state/selections/SelectionState";
import { selectSelectedAccountEntityId, selectSelectedBenchmarkEntityId, selectSelectedAccountEntity } from 'state/selections/selectors';
import { getEntitySummariesObject, getEntitySummariesObjectForBench } from 'state/util';
import scope from "./scope";
import { memoize, uniqueByKey } from '@ls/js-utils';
import { getFlattenedNodes, SummaryType } from '@ls/grouping-service';

export const selectData = (summaryType) => state => state[scope]['data'][summaryType];
export const selectFetchingData = (summaryType) => state => state[scope]['fetchingData'][summaryType];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return state => state[scope]['definitions'][selectionState];
  }
  return createSelector(
    state => state[scope]['definitions'][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(({ complete }) => complete);
      return completeDefinitions.filter(uniqueByKey(completeDefinitions, 'key'));
    },
  );
});

const findIssueCount = (node, definition, accEntityId, bmkEntityId, benchId) => {
  const { issueCountType, calcType } = definition;
  const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
  const benchValue = calcType === CalcType.Benchmark ? 0 : benchId;
  const isRelative = calcType === CalcType.Relative;
  let entitySummariesObject;
  if (benchValue !== null && benchValue !== undefined) {
    entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative);
  } else {
    entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
  }

  const issueCounts = entitySummariesObject?.issueCounts ?? [];
  const betaSummaryObject = issueCounts?.find(issueCountObject => {
    return issueCountObject.type === issueCountType
  });
  return betaSummaryObject?.issueCount ?? null;
};

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absData, relData, accEntityId, bmkEntityId, benchId) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    return [...absoluteNodes, ...relativeNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          row[definition.key] = findIssueCount(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId,
          );
        });
      return rowMap;
    }, {});
  }
);

